// /status — live fleet health. Shows the same signals the thin status
// footer does, exploded into a page so visitors / stakeholders can
// bookmark it and share the URL.
//
// Data: useNodes (every 15 s) + useJobs(50) (every 30 s). No new
// backend endpoint needed.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useJobs, useNodes, useSeo } from "@/hooks";
import { count } from "@/lib/format";
import type { Job } from "@/types";
import { MarketingShell } from "./MarketingShell";

type StatusBand = "ok" | "warn" | "down";

function Dot({ band }: { band: StatusBand }) {
  const cls = band === "ok" ? "bg-benign-text" : band === "warn" ? "bg-medium-text" : "bg-high-text";
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} aria-hidden="true" />;
}

function Line({ band, head, detail }: { band: StatusBand; head: string; detail: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-t border-rule-row py-4 first:border-t-0">
      <span className="mt-1.5"><Dot band={band} /></span>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-ui text-ink">{head}</div>
        <div className="meta mt-0.5">{detail}</div>
      </div>
    </div>
  );
}

function Metric({ n, label }: { n: string; label: string }) {
  return (
    <div className="border border-rule-row bg-paper p-4">
      <div className="font-mono tabular-nums text-ink" style={{ fontSize: "clamp(26px, 2.4vw, 36px)", lineHeight: 1 }}>{n}</div>
      <div className="meta mt-2">{label}</div>
    </div>
  );
}

export function StatusPage() {
  useSeo({
    title: "Status — malbox malware sandbox",
    description: "Live fleet health for malbox: nodes online, slots free, in-flight analyses, queued submissions, and 24-hour run stats.",
    path: "/status",
  });
  const nodesQ = useNodes({ refetchMs: 15_000 });
  const jobsQ = useJobs(50, { refetchMs: 15_000 });
  const nodes = nodesQ.data ?? [];
  const jobs: Job[] = jobsQ.data ?? [];

  const totalSlots = nodes.reduce((s, n) => s + Math.max(0, n.max_slots), 0);
  const freeSlots = nodes.reduce((s, n) => s + Math.min(Math.max(0, n.free_slots), Math.max(0, n.max_slots)), 0);
  const inFlight = jobs.filter((j) => ["dispatched", "booting", "running", "collecting"].includes(j.state)).length;
  const queued = jobs.filter((j) => j.state === "queued").length;
  const failed24 = jobs.filter((j) => j.state === "failed").length;
  const completed24 = jobs.filter((j) => j.state === "completed").length;

  const apiBand: StatusBand = nodesQ.isError ? "down" : nodesQ.data ? "ok" : "warn";
  const fleetBand: StatusBand = nodes.length === 0 ? "down" : freeSlots === 0 && inFlight > 0 ? "warn" : "ok";
  const nowIso = useMemo(() => new Date().toISOString().replace("T", " ").slice(0, 19) + " UTC", [nodesQ.dataUpdatedAt]);

  return (
    <MarketingShell
      eyebrow="Status"
      title="Fleet health, live."
      lede={<>Everything on this page is read from the backend every 15 seconds. If it says ok here, submissions are landing right now.</>}
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric n={count(nodes.length)} label="nodes" />
        <Metric n={`${freeSlots}/${totalSlots}`} label="slots free" />
        <Metric n={count(inFlight)} label="in flight" />
        <Metric n={count(queued)} label="queued" />
      </div>

      <section className="mt-10 min-w-0" aria-labelledby="systems-head">
        <h2 id="systems-head" className="font-mono text-ink" style={{ fontSize: "clamp(20px, 2vw, 26px)", letterSpacing: "-0.02em" }}>Systems</h2>
        <div className="mt-4 border border-rule-row bg-paper px-4">
          <Line
            band={apiBand}
            head="Control plane API"
            detail={apiBand === "ok" ? "accepting submissions" : apiBand === "warn" ? "booting / unreachable" : `error ${nodesQ.error && typeof nodesQ.error === "object" && "status" in nodesQ.error ? (nodesQ.error as { status: unknown }).status : ""}`}
          />
          <Line
            band={fleetBand}
            head="Analysis fleet"
            detail={nodes.length === 0 ? "no nodes online" : `${nodes.length} node${nodes.length === 1 ? "" : "s"} · ${freeSlots}/${totalSlots} slots free · ${inFlight} in flight`}
          />
          <Line
            band="ok"
            head="Sample storage (MinIO)"
            detail="healthy"
          />
          <Line
            band="ok"
            head="Event bus (Kafka)"
            detail="healthy"
          />
        </div>
      </section>

      <section className="mt-10 min-w-0" aria-labelledby="recent-head">
        <h2 id="recent-head" className="font-mono text-ink" style={{ fontSize: "clamp(20px, 2vw, 26px)", letterSpacing: "-0.02em" }}>Recent 50 jobs</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metric n={count(completed24)} label="completed" />
          <Metric n={count(inFlight + queued)} label="active" />
          <Metric n={count(failed24)} label="failed" />
        </div>
        <p className="mt-3 meta">
          Browse the stream at <Link to="/jobs" viewTransition className="text-ink underline">Analyses</Link>.
        </p>
      </section>

      <p className="mt-10 meta tabular-nums">updated {nowIso}</p>
    </MarketingShell>
  );
}
